import { useEffect, useRef, useState } from 'react'
import { Camera } from '@/types/colmap'
import { OffsetMap } from '@/types/offsetMap'
import './FrameRelationshipBalloon.css'

interface FrameRelationshipBalloonProps {
  fromFrameIndex: number
  toFrameIndex: number
  offsetMap: OffsetMap | null
  cameras: Camera[]
  onClose: () => void
}

export function FrameRelationshipBalloon({
  fromFrameIndex,
  toFrameIndex,
  offsetMap,
  cameras,
  onClose,
}: FrameRelationshipBalloonProps) {
  const balloonRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragStateRef = useRef({ startX: 0, startY: 0, lastX: 0, lastY: 0, offsetX: 0, offsetY: 0 })

  // Extract relationship data organized by section
  const getRelationshipData = () => {
    const sections: { section: string; icon: string; items: { label: string; value: string; highlight?: boolean }[] }[] = []

    if (offsetMap) {
      const keyForward = `${fromFrameIndex}_${toFrameIndex}`
      const keyReverse = `${toFrameIndex}_${fromFrameIndex}`

      // Check all_pairs (all connected frame pairs)
      let offset = offsetMap.all_pairs?.[keyForward] || offsetMap.all_pairs?.[keyReverse]

      if (offset) {
        // Position & Distance Section
        sections.push({
          section: 'POSITION & DISTANCE',
          icon: '📍',
          items: [
            { label: 'Offset X', value: `${offset.dx.toFixed(0)} px` },
            { label: 'Offset Y', value: `${offset.dy.toFixed(0)} px` },
            ...(offset.dz !== undefined ? [{ label: 'dZ (Z-comp)', value: `${offset.dz.toFixed(2)}` }] : []),
            { label: '3D Distance', value: `${offset.distance_3d?.toFixed(2) ?? '–'} units`, highlight: true }
          ]
        })

        // Orientation Section
        const hasAngles = offset.yaw_diff !== undefined || offset.pitch_diff !== undefined || offset.roll_diff !== undefined
        if (hasAngles) {
          sections.push({
            section: 'ORIENTATION',
            icon: '🔄',
            items: [
              ...(offset.yaw_diff !== undefined ? [{ label: 'Yaw Δ', value: `${offset.yaw_diff.toFixed(1)}°` }] : []),
              ...(offset.pitch_diff !== undefined ? [{ label: 'Pitch Δ', value: `${offset.pitch_diff.toFixed(1)}°` }] : []),
              ...(offset.roll_diff !== undefined ? [{ label: 'Roll Δ', value: `${offset.roll_diff.toFixed(1)}°` }] : []),
              ...(offset.angle_diff !== undefined ? [{ label: 'Angle Δ', value: `${offset.angle_diff.toFixed(1)}°`, highlight: true }] : []),
              ...(offset.similar_orientation !== undefined ? [{ label: 'Similar Orient', value: offset.similar_orientation ? '✓ Yes' : '✗ No' }] : [])
            ]
          })
        }

        // Connection Section
        sections.push({
          section: 'CONNECTION',
          icon: '🔗',
          items: [
            { label: 'Connection', value: 'Connected' },
            ...(offset.motion_type ? [{ label: 'Motion Type', value: offset.motion_type }] : []),
            { label: 'Confidence', value: `${(offset.confidence * 100).toFixed(0)}%` },
            { label: 'Shared Points', value: `${offset.pointCount}` }
          ]
        })
      } else {
        // Fallback: use camera positions if no offsetMap data
        const fromCam = cameras[fromFrameIndex]
        const toCam = cameras[toFrameIndex]
        if (fromCam?.position && toCam?.position) {
          const [x1, y1, z1] = fromCam.position
          const [x2, y2, z2] = toCam.position
          const distance = Math.sqrt(
            (x2 - x1) ** 2 + (y2 - y1) ** 2 + (z2 - z1) ** 2
          )
          sections.push({
            section: 'POSITION & DISTANCE',
            icon: '📍',
            items: [
              { label: '3D Distance', value: `${distance.toFixed(2)} units (camera)`, highlight: true }
            ]
          })
        }
        
        sections.push({
          section: 'CONNECTION',
          icon: '🔗',
          items: [{ label: 'Connection', value: 'Not directly connected' }]
        })
      }
    } else {
      // No offsetMap, use camera positions
      const fromCam = cameras[fromFrameIndex]
      const toCam = cameras[toFrameIndex]
      if (fromCam?.position && toCam?.position) {
        const [x1, y1, z1] = fromCam.position
        const [x2, y2, z2] = toCam.position
        const distance = Math.sqrt(
          (x2 - x1) ** 2 + (y2 - y1) ** 2 + (z2 - z1) ** 2
        )
        sections.push({
          section: 'POSITION & DISTANCE',
          icon: '📍',
          items: [
            { label: '3D Distance', value: `${distance.toFixed(2)} units (camera)`, highlight: true }
          ]
        })
      }
    }

    return sections
  }

  const relationshipData = getRelationshipData()
  
  console.log(`🎈 [FrameRelationshipBalloon] Rendering balloon for frames ${fromFrameIndex} → ${toFrameIndex}`)
  console.log(`   Looking for offset_map keys: ${fromFrameIndex}_${toFrameIndex} OR ${toFrameIndex}_${fromFrameIndex}`)
  if (offsetMap?.all_pairs) {
    const keyForward = `${fromFrameIndex}_${toFrameIndex}`
    const keyReverse = `${toFrameIndex}_${fromFrameIndex}`
    const foundForward = offsetMap.all_pairs[keyForward]
    const foundReverse = offsetMap.all_pairs[keyReverse]
    console.log(`   Found forward (${keyForward}): ${foundForward ? 'YES' : 'NO'}`)
    console.log(`   Found reverse (${keyReverse}): ${foundReverse ? 'YES' : 'NO'}`)
    if (foundForward) {
      console.log(`   Forward data: dx=${foundForward.dx?.toFixed(2)}, dy=${foundForward.dy?.toFixed(2)}, dz=${foundForward.dz?.toFixed(4)}`)
    }
    if (foundReverse) {
      console.log(`   Reverse data: dx=${foundReverse.dx?.toFixed(2)}, dy=${foundReverse.dy?.toFixed(2)}, dz=${foundReverse.dz?.toFixed(4)}`)
    }
  }
  
  // Handle drag start on header
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!balloonRef.current) return
    
    // Get current transform offset if any
    const transform = balloonRef.current.style.transform
    let currentOffsetX = 0
    let currentOffsetY = 0
    
    if (transform && transform.includes('translate')) {
      // Try calc() pattern first: translate(calc(-50% + 100px), calc(-50% + 50px))
      let match = transform.match(/translate\(calc\([^)]+\s*\+\s*([-\d.]+)px\),\s*calc\([^)]+\s*\+\s*([-\d.]+)px\)\)/)
      if (match) {
        currentOffsetX = parseFloat(match[1])
        currentOffsetY = parseFloat(match[2])
      } else {
        // Fallback to simple translate: translate(100px, 50px)
        match = transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)/)
        if (match) {
          currentOffsetX = parseFloat(match[1])
          currentOffsetY = parseFloat(match[2])
        }
      }
    }
    
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      offsetX: currentOffsetX,
      offsetY: currentOffsetY
    }
    setIsDragging(true)
  }

  // Handle drag movement
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!balloonRef.current) return

      // Calculate delta from last position
      const deltaX = e.clientX - dragStateRef.current.lastX
      const deltaY = e.clientY - dragStateRef.current.lastY

      // Update offset and last position
      dragStateRef.current.offsetX += deltaX
      dragStateRef.current.offsetY += deltaY
      dragStateRef.current.lastX = e.clientX
      dragStateRef.current.lastY = e.clientY

      // Apply position with transform: centering + offset
      balloonRef.current.style.transform = `translate(calc(-50% + ${dragStateRef.current.offsetX}px), calc(-50% + ${dragStateRef.current.offsetY}px))`
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging])
  
  // Removed: click-outside handler that closed the balloon
  // Now the balloon only closes when X button is clicked

  return (
    <div
      ref={balloonRef}
      className="frame-relationship-balloon"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Close button */}
      <button
        className="balloon-close"
        onClick={onClose}
        title="Close"
        aria-label="Close"
      >
        ✕
      </button>

      {/* Content */}
      <div className="balloon-content">
        <div 
          ref={headerRef}
          className="balloon-header"
          onMouseDown={handleMouseDown}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <div>Frame {fromFrameIndex} → Frame {toFrameIndex}</div>
          <div style={{ fontSize: '10px', color: '#8b949e', marginTop: '2px', fontWeight: 'normal' }}>
            {cameras[fromFrameIndex]?.frame_file ? `${cameras[fromFrameIndex].frame_file} → ` : ''}
            {cameras[toFrameIndex]?.frame_file || ''}
          </div>
        </div>

        <div className="balloon-body">
          {relationshipData.map((section, sIdx) => (
            <div key={sIdx} className="balloon-section">
              <div className="balloon-section-header">
                <span className="balloon-icon">{section.icon}</span>
                <span className="balloon-section-title">{section.section}</span>
              </div>
              <div className="balloon-section-items">
                {section.items.map((item, iIdx) => (
                  <div key={iIdx} className={`balloon-row ${item.highlight ? 'highlight' : ''}`}>
                    <span className="balloon-label">{item.label}:</span>
                    <span className="balloon-value">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
